import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, animate } from "framer-motion";
import {
  Activity,
  ArrowRight,
  BrainCircuit,
  CheckCircle2,
  ChevronDown,
  CircuitBoard,
  Eye,
  Gauge,
  Layers3,
  LineChart,
  Menu,
  Phone,
  RadioTower,
  RefreshCw,
  Workflow,
  X,
  Zap,
} from "lucide-react";
import "./styles.css";

/* ─── data ────────────────────────────────────────────────────── */

const proof = [
  {
    icon: Zap,
    title: "Real-time",
    text: "Machine events, defects, alarms, and production gaps are surfaced as they happen.",
  },
  {
    icon: Layers3,
    title: "Interoperable",
    text: "Works across existing PLCs, sensors, cameras, gateways, dashboards, and factory machines.",
  },
  {
    icon: RefreshCw,
    title: "Updatable",
    text: "Models, thresholds, workflows, and reports evolve as your factory process changes.",
  },
];

const products = [
  {
    icon: CircuitBoard,
    title: "Industry IoT Systems",
    text: "Sensor networks, PLC integrations, edge gateways, and local dashboards for live machine visibility.",
    image: "https://images.unsplash.com/photo-1581092335397-9583eb92d232?auto=format&fit=crop&w=720&q=72",
    imageAlt: "Technician working with industrial automation equipment",
  },
  {
    icon: Eye,
    title: "Computer Vision QC",
    text: "Camera-based inspection for defects, missing parts, dimensional checks, and production verification.",
    image: "https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=720&q=72",
    imageAlt: "Automated machine vision inspection setup on a production line",
  },
  {
    icon: Activity,
    title: "Predictive Maintenance",
    text: "Sensor-driven anomaly detection that helps teams act before breakdowns stop production.",
    image: "https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?auto=format&fit=crop&w=720&q=72",
    imageAlt: "Engineer inspecting machine components and sensor data",
  },
  {
    icon: LineChart,
    title: "Profit Intelligence",
    text: "Machine performance, downtime, rejects, energy, and throughput data converted into practical decisions.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=720&q=72",
    imageAlt: "Industrial analytics dashboard with performance charts",
  },
];

const stats = [
  ["40%", "downtime reduction target"],
  ["24/7", "machine visibility"],
  ["3x", "faster defect detection"],
  ["ROI", "profit-first automation"],
];

const process = [
  {
    icon: Gauge,
    title: "Study",
    text: "Map machines, bottlenecks, downtime causes, rejection patterns, data sources, and profit leakage.",
  },
  {
    icon: RadioTower,
    title: "Connect",
    text: "Integrate sensors, PLCs, cameras, gateways, edge devices, and plant dashboards with minimal disruption.",
  },
  {
    icon: BrainCircuit,
    title: "Model",
    text: "Deploy AI, computer vision, and anomaly rules for quality, maintenance, cycle time, and throughput.",
  },
  {
    icon: Workflow,
    title: "Operate",
    text: "Give supervisors live alerts, reports, workflows, and decision views that keep machines productive.",
  },
];

const quality = [
  "Real-time machine health, cycle time, output, and downtime tracking",
  "Computer vision inspection for repeatable quality control",
  "Predictive alerts for vibration, temperature, load, current, and process anomalies",
  "Production insights designed to maximise profit and minimise unplanned stoppage",
];

const industries = [
  "Large-scale manufacturing plants",
  "Automotive component lines",
  "CNC and fabrication units",
  "Packaging and process industries",
  "Quality inspection stations",
  "Energy and utility monitoring",
];

const team = [
  {
    name: "Team Member 1",
    role: "Industrial Automation Advisor",
    text: "Defines the factory transformation roadmap, aligns technical deployment with business ROI, and works closely with plant leadership.",
    image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=360&q=72",
  },
  {
    name: "Team Member 2",
    role: "Computer Vision Engineer",
    text: "Builds inspection models, defect detection pipelines, and edge AI workflows for real-time quality control on production lines.",
    image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=360&q=72",
  },
  {
    name: "Team Member 3",
    role: "Sensors & Edge Integration",
    text: "Connects PLCs, sensors, gateways, and machine telemetry into reliable data systems for monitoring and predictive maintenance.",
    image: "https://images.unsplash.com/photo-1581092795360-fd1ca04f0952?auto=format&fit=crop&w=360&q=72",
  },
  {
    name: "Team Member 4",
    role: "Dashboards & Data Systems",
    text: "Designs dashboards, alerts, analytics layers, and reporting tools that convert machine data into clear operational decisions.",
    image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=360&q=72",
  },
];

/* ─── shared variants ─────────────────────────────────────────── */

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut" as const },
  },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { duration: 0.6, ease: "easeOut" as const },
  },
};

const slideLeft = {
  hidden: { opacity: 0, x: -36 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" as const },
  },
};

const slideRight = {
  hidden: { opacity: 0, x: 36 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" as const },
  },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const staggerSlow = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14 } },
};

const cardIn = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" as const },
  },
};

/* ─── stat counter component ──────────────────────────────────── */

const StatItem = ({ value, label }: { value: string; label: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: false, margin: "-60px" });

  // Numeric stats count up; text/symbol ones just fade in
  const numericMatch = value.match(/^(\d+)(%|x)?$/i);
  const [display, setDisplay] = useState(numericMatch ? "0" + (numericMatch[2] ?? "") : value);

  useEffect(() => {
    if (!isInView || !numericMatch) return;
    const end = parseInt(numericMatch[1], 10);
    const suffix = numericMatch[2] ?? "";
    const controls = animate(0, end, {
      duration: 1.4,
      ease: "easeOut",
      onUpdate(v) {
        setDisplay(Math.round(v) + suffix);
      },
    });
    return () => controls.stop();
  }, [isInView]); // eslint-disable-line

  return (
    <motion.div
      className="stat"
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeUp}
    >
      <strong>{display}</strong>
      <span>{label}</span>
    </motion.div>
  );
};

/* ─── app ─────────────────────────────────────────────────────── */

const App = () => {
  const companyName = "Auronix";
  const phoneNumber = "6362325461";

  const [bannerVisible, setBannerVisible] = useState(true);
  const [bannerExiting, setBannerExiting] = useState(false);
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const industriesRef = useRef<HTMLDivElement>(null);
  const bannerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScrollY = useRef(0);

  const showBanner = () => {
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    setBannerExiting(false);
    setBannerVisible(true);
    bannerTimerRef.current = setTimeout(() => {
      setBannerExiting(true);
      setTimeout(() => setBannerVisible(false), 600);
    }, 15000);
  };

  const dismissBanner = () => {
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    setBannerExiting(true);
    setTimeout(() => setBannerVisible(false), 600);
  };

  useEffect(() => {
    showBanner();
    const handleScroll = () => {
      const currentY = window.scrollY;
      const atTop = currentY === 0;
      if (atTop && lastScrollY.current === 0 && !bannerVisible) showBanner();
      lastScrollY.current = currentY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      if (currentY > 0 && bannerVisible) {
        dismissBanner();
        return;
      }
      if (currentY === 0 && lastScrollY.current === 0 && !bannerVisible) showBanner();
      lastScrollY.current = currentY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [bannerVisible]); // eslint-disable-line

  // Close industries dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (industriesRef.current && !industriesRef.current.contains(e.target as Node)) {
        setIndustriesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <main className="site-shell">
      {/* ── Banner ───────────────────────────────────────────────── */}
      {bannerVisible && (
        <div
          className={`floating-banner${bannerExiting ? " floating-banner--exit" : ""}`}
          role="banner"
        >
          <div className="floating-banner__inner">
            <h2 className="floating-banner__title">
              Make Your Factory More Profitable and Reliable
            </h2>
            <p className="floating-banner__text">
              We help factory owners like you reduce machine downtime, improve product quality,
              and increase profits—using easy-to-understand technology that works with your
              existing machines. No technical knowledge needed. We handle the details, you see
              the results.
            </p>
          </div>
          <button
            className="floating-banner__close"
            aria-label="Dismiss banner"
            onClick={dismissBanner}
          >
            ✕
          </button>
        </div>
      )}

      {/* ── Nav ──────────────────────────────────────────────────── */}
      <motion.nav
        className="nav"
        aria-label="Main navigation"
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <a className="brand" href="#top" aria-label={`${companyName} home`}>
          <span className="brand__mark">AX</span>
          <span>
            {companyName}
            <small>Industrial Intelligence</small>
          </span>
        </a>
        <div className="nav__links">
          <a href="#platform">Platform</a>
          <a href="#solutions">Solutions</a>
          <a href="#impact">Impact</a>
          <div
            className={`nav__dropdown${industriesOpen ? " nav__dropdown--open" : ""}`}
            ref={industriesRef}
          >
            <button
              className="nav__dropdown-trigger"
              onClick={() => setIndustriesOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={industriesOpen}
            >
              Industries <ChevronDown size={15} />
            </button>
            <ul className="nav__dropdown-menu" role="listbox">
              <li>
                <Link to="/industries/automotive" onClick={() => setIndustriesOpen(false)}>
                  Automotive Part Manufacturing
                </Link>
              </li>
              <li>
                <Link to="/industries/textile" onClick={() => setIndustriesOpen(false)}>
                  Textile Industry
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <button
          className="nav__hamburger"
          onClick={() => setMobileMenuOpen((o) => !o)}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </motion.nav>

      {/* ── Mobile nav ── */}
      {mobileMenuOpen && (
        <div className="mobile-nav">
          <a href="#platform" onClick={() => setMobileMenuOpen(false)}>Platform</a>
          <a href="#solutions" onClick={() => setMobileMenuOpen(false)}>Solutions</a>
          <a href="#impact" onClick={() => setMobileMenuOpen(false)}>Impact</a>
          <div className="mobile-nav__divider" />
          <p className="mobile-nav__group-label">Industries</p>
          <Link to="/industries/automotive" className="mobile-nav__sub" onClick={() => setMobileMenuOpen(false)}>
            Automotive Part Manufacturing
          </Link>
          <Link to="/industries/textile" className="mobile-nav__sub" onClick={() => setMobileMenuOpen(false)}>
            Textile Industry
          </Link>
        </div>
      )}

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="hero" aria-labelledby="hero-title" id="top">
        <div className="hero-blur hero-blur--one" />
        <div className="hero-blur hero-blur--two" />

        <motion.div
          className="hero__content"
          initial="hidden"
          animate="visible"
          variants={stagger}
        >
          <motion.p className="eyebrow" variants={fadeUp}>
            For Factory Owners
          </motion.p>

          <motion.h1 id="hero-title" variants={fadeUp}>
            Less Downtime.
            <span> More Profit.</span>
            <br />
            Intelligent Factory Operations.
          </motion.h1>

          <motion.p className="hero__lead" variants={fadeUp}>
            We connect your machines, monitor production in real-time, detect problems before
            breakdowns happen, and help your factory run more profitably.
          </motion.p>

          <motion.div className="hero__actions" variants={fadeUp}>
            <a className="button button--primary" href="#contact">
              Book a factory study
            </a>
            <a className="button button--ghost" href="#platform">
              Explore platform
            </a>
          </motion.div>
        </motion.div>

        {/* Hero image — fades + drifts up, then bobs gently */}
        <motion.div
          className="hero__visual"
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.25, 0.1, 0.25, 1], delay: 0.2 }}
        >
          <motion.div
            className="hero-image-wrapper"
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          >
            <img src="/factory.png" alt="Factory" />
          </motion.div>
        </motion.div>
      </section>

      {/* ── Proof band ───────────────────────────────────────────── */}
      <motion.section
        className="proof-band"
        aria-label="Why Auronix"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, margin: "-80px" }}
        variants={stagger}
      >
        <motion.div className="section__intro proof-band__intro" variants={fadeUp}>
          <p className="eyebrow eyebrow--dark">Why Choose Us?</p>
          <h2>Instant, On-Site Factory Insights—No Complicated Tech Needed</h2>
        </motion.div>

        <motion.div className="proof-grid" variants={stagger}>
          {proof.map(({ icon: Icon, title, text }) => (
            <motion.article className="proof-card" key={title} variants={cardIn}>
              <Icon size={22} aria-hidden="true" />
              <h3>{title}</h3>
              <p>{text}</p>
            </motion.article>
          ))}
        </motion.div>
      </motion.section>

      {/* ── Platform ─────────────────────────────────────────────── */}
      <section className="platform" id="platform">
        <motion.div
          className="platform__copy"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-80px" }}
          variants={slideLeft}
        >
          <p className="eyebrow eyebrow--dark">How It Works</p>
          <h2>We Use What You Already Have</h2>
          <p>
            No need to buy new machines. We connect to your current equipment, add simple
            sensors or cameras if needed, and give you a live view of your factory's health,
            problems, and opportunities to save money.
          </p>
          <a className="text-link" href="#solutions">
            See how it works
            <ArrowRight size={17} />
          </a>
        </motion.div>

        <motion.div
          className="platform__image"
          aria-label="Connected industrial AI platform illustration"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-80px" }}
          variants={slideRight}
        >
          <img
            src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?auto=format&fit=crop&w=900&q=80"
            alt="Large-scale manufacturing plant with industrial IoT sensors and automated machinery"
            loading="lazy"
            decoding="async"
          />
        </motion.div>
      </section>

      {/* ── Stats ────────────────────────────────────────────────── */}
      <section className="stats" aria-label="Company highlights">
        {stats.map(([value, label]) => (
          <StatItem key={value} value={value} label={label} />
        ))}
      </section>

      {/* ── Solutions / Products ─────────────────────────────────── */}
      <section className="section" id="solutions">
        <motion.div
          className="section__intro"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-80px" }}
          variants={fadeUp}
        >
          <p className="eyebrow eyebrow--dark">What You Get</p>
          <h2>See Problems Early. Fix Them Fast. Grow Your Profits.</h2>
          <p>
            Our solutions help you and your team spot issues before they become expensive,
            keep machines running, and make better decisions every day—without needing to be
            a tech expert.
          </p>
        </motion.div>

        <motion.div
          className="product-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-60px" }}
          variants={stagger}
        >
          {products.map(({ icon: Icon, title, text, image, imageAlt }) => (
            <motion.article
              className="product"
              key={title}
              variants={cardIn}
              whileHover={{ y: -6, transition: { duration: 0.22, ease: "easeOut" } }}
            >
              <div className="product__media">
                <img src={image} alt={imageAlt} />
              </div>
              <div className="product__body">
                <span className="product__icon">
                  <Icon size={22} />
                </span>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </section>

      {/* ── Workflow ─────────────────────────────────────────────── */}
      <section className="workflow-section">
        <motion.div
          className="section__intro"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-80px" }}
          variants={fadeUp}
        >
          <p className="eyebrow eyebrow--dark">How We Work With You</p>
          <h2>Step-by-Step: From Factory Walkthrough to Results</h2>
        </motion.div>

        <motion.div
          className="workflow-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-60px" }}
          variants={staggerSlow}
        >
          {process.map(({ icon: Icon, title, text }, index) => (
            <motion.article className="workflow-card" key={title} variants={cardIn}>
              <span className="workflow-card__count">0{index + 1}</span>
              <Icon size={24} />
              <h3>{title}</h3>
              <p>{text}</p>
            </motion.article>
          ))}
        </motion.div>
      </section>

      {/* ── Impact ───────────────────────────────────────────────── */}
      <section className="impact" id="impact">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-80px" }}
          variants={slideLeft}
        >
          <p className="eyebrow eyebrow--dark">Business Results</p>
          <h2>What Our Customers Achieve</h2>
          <p>
            Our clients see fewer breakdowns, faster problem-solving, better product quality,
            and higher profits—without needing to learn new technology.
          </p>
        </motion.div>

        <motion.ul
          className="impact__list"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-60px" }}
          variants={staggerSlow}
        >
          {quality.map((item) => (
            <motion.li key={item} variants={cardIn}>
              <CheckCircle2 size={20} />
              {item}
            </motion.li>
          ))}
        </motion.ul>
      </section>

      {/* ── Industries ───────────────────────────────────────────── */}
      <section className="section section--compact">
        <motion.div
          className="section__intro"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-80px" }}
          variants={fadeUp}
        >
          <p className="eyebrow eyebrow--dark">Who Is This For?</p>
          <h2>Factories That Want Less Downtime and More Profit</h2>
        </motion.div>

        <motion.div
          className="industry-cards"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-60px" }}
          variants={stagger}
        >
          <motion.div variants={cardIn}>
            <Link to="/industries/automotive" className="industry-card">
              <span className="industry-card__label">Automotive Part Manufacturing</span>
              <ArrowRight size={20} className="industry-card__arrow" />
            </Link>
          </motion.div>
          <motion.div variants={cardIn}>
            <Link to="/industries/textile" className="industry-card">
              <span className="industry-card__label">Textile Industry</span>
              <ArrowRight size={20} className="industry-card__arrow" />
            </Link>
          </motion.div>
        </motion.div>
      </section>

      {/* ── Team ─────────────────────────────────────────────────── */}
      {/* Hidden temporarily */}
      {false && <section className="section team-section" id="team">
        <motion.div
          className="section__intro"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-80px" }}
          variants={fadeUp}
        >
          <p className="eyebrow eyebrow--dark">Our Team</p>
          <h2>Experts Who Understand Factories and Business</h2>
          <p>
            Our team combines factory experience with technology know-how. We focus on making
            your business more successful, not just adding gadgets.
          </p>
        </motion.div>

        <motion.div
          className="team-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-60px" }}
          variants={stagger}
        >
          {team.map((member) => (
            <motion.article
              className="team-card"
              key={member.name}
              variants={cardIn}
              whileHover={{ y: -4, transition: { duration: 0.2, ease: "easeOut" } }}
            >
              <div className="team-card__profile">
                <img src={member.image} alt={member.name} loading="lazy" decoding="async" />
                <strong>{member.name}</strong>
              </div>
              <div className="team-card__banner">
                <p>{member.role}</p>
                <h3>{member.role}</h3>
                <span>{member.text}</span>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </section>}

      {/* ── Contact ──────────────────────────────────────────────── */}
      <motion.section
        className="contact"
        id="contact"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, margin: "-80px" }}
        variants={stagger}
      >
        <motion.div variants={fadeUp}>
          <p className="eyebrow eyebrow--dark">Book a custom demo</p>
          <h2>Tell us what is slowing your factory down.</h2>
          <p>
            Share your machine type, production line, downtime issue, rejection rate, or
            monitoring gap. We will suggest a practical roadmap for sensors, AI, vision, and
            dashboards.
          </p>
        </motion.div>

        <motion.div className="contact__actions" variants={fadeUp}>
          <a className="button button--light" href={`tel:+91${phoneNumber}`}>
            <Phone size={18} />
            {phoneNumber}
          </a>
          <a className="button button--outline" href="mailto:hello@auronix.ai">
            Request consultation
          </a>
        </motion.div>
      </motion.section>

      {/* ── Footer ───────────────────────────────────────────────── */}
      <motion.footer
        className="footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: false }}
        transition={{ duration: 0.5 }}
      >
        <span>{companyName} Industrial Intelligence</span>
        <span>Machine monitoring, computer vision, automation, and performance intelligence</span>
      </motion.footer>
    </main>
  );
};

export default App;